import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPhone,
  faEnvelope,
  faCopy,
  faLocation,
} from "@fortawesome/free-solid-svg-icons";
import Header from "../../components/hotels/Header";
import { config } from "../../config";
import { format, parseISO } from "date-fns";
import { calculateFinalPrice, formatPrice } from "../../utils/pricing";

function BookingConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const booking = location.state?.booking?.data;
  const isNewBooking = location.state?.fromBookingSummary || false;
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    console.log("Booking data:", booking);
  }, [booking]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === booking.accommodation.photos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const previousPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === 0 ? booking.accommodation.photos.length - 1 : prevIndex - 1
    );
  };

  // If no booking data, redirect to hotels page
  if (!booking) {
    return <Navigate to="/hotels" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Booking Confirmed!
            </h1>
            <p className="text-gray-600">
              Booking reference: {booking.reference}
            </p>
          </div>

          {/* Photo Carousel */}
          {booking.accommodation.photos &&
          booking.accommodation.photos.length > 0 ? (
            <div className="relative mb-8 rounded-lg overflow-hidden">
              <img
                src={booking.accommodation.photos[currentPhotoIndex].url}
                alt={`Property view ${currentPhotoIndex + 1}`}
                className="w-full h-64 object-cover"
              />
              {booking.accommodation.photos.length > 1 && (
                <>
                  <button
                    onClick={previousPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                    aria-label="Previous photo"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                    aria-label="Next photo"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                    {currentPhotoIndex + 1} /{" "}
                    {booking.accommodation.photos.length}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="mb-8 rounded-lg bg-gray-100 h-64 flex items-center justify-center">
              <p className="text-gray-500">No photos available</p>
            </div>
          )}

          {/* Guest Details */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-3">Guest Details</h2>
              <div className="bg-gray-50 rounded p-4 space-y-2">
                <p>
                  <span className="font-medium">Name:</span>{" "}
                  {booking.guests[0].given_name} {booking.guests[0].family_name}
                </p>
                <p>
                  <span className="font-medium">Email:</span> {booking.email}
                </p>
                <p>
                  <span className="font-medium">Phone:</span>{" "}
                  {booking.phone_number}
                </p>
              </div>
            </div>

            {/* Stay Details */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Stay Details</h2>
              <div className="bg-gray-50 rounded p-4 space-y-2">
                <p className="font-bold text-xl">
                  {booking.accommodation.name}
                </p>
                <div>
                  <FontAwesomeIcon icon={faLocation} />{" "}
                  {booking.accommodation.location.address.line_one},{" "}
                  {booking.accommodation.location.address.city_name},{" "}
                  {booking.accommodation.location.address.postal_code}
                </div>
                <div className="flex flex-col p-2 border rounded-lg gap-2 w-fit bg-white">
                  <p>
                    <span className="font-medium">Check-in:</span>{" "}
                    {new Date(booking.check_in_date).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-light">Check-in after:</span>{" "}
                    {
                      booking.accommodation.check_in_information
                        .check_in_after_time
                    }
                  </p>
                  <p>
                    <span className="font-light">Check-in before:</span>{" "}
                    {
                      booking.accommodation.check_in_information
                        .check_in_before_time
                    }
                  </p>
                </div>
                <div className="flex flex-col p-2 border rounded-lg gap-2 w-fit bg-white">
                  <p>
                    <span className="font-medium">Check-out:</span>{" "}
                    {new Date(booking.check_out_date).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-light">Check-out before:</span>{" "}
                    {
                      booking.accommodation.check_in_information
                        .check_out_before_time
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Room Details */}
            <div>
              <h2 className="text-lg font-semibold mb-3">
                Room & Guest Details
              </h2>
              <div className="bg-gray-50 rounded p-4 space-y-4">
                <div className="space-y-2">
                  <p className="font-medium">
                    {booking.rooms} {booking.rooms === 1 ? "Room" : "Rooms"}{" "}
                    Booked
                  </p>
                  {booking.accommodation.rooms.map((room, index) => (
                    <div
                      key={index}
                      className="bg-white p-3 rounded-lg shadow-sm"
                    >
                      <p className="font-medium">{room.name}</p>
                      <p className="text-sm text-gray-600">
                        Beds:{" "}
                        {room.beds
                          .map((bed) => `${bed.count} ${bed.type}`)
                          .join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Payment Details</h2>
              <div className="bg-gray-50 rounded p-4 space-y-2">
                <p className="text-blue-800 font-medium">
                  Amount paid:{" "}
                  {formatPrice(
                    calculateFinalPrice(
                      booking.accommodation.rooms[0].rates[0].total_amount,
                      true
                    ),
                    booking.accommodation.rooms[0].rates[0].total_currency
                  )}
                </p>
                {booking.accommodation.rooms[0].rates[0]
                  .due_at_accommodation_amount === "0.00" ? (
                  <p className="text-green-800 font-medium">
                    You're all paid up, nothing due at property
                  </p>
                ) : (
                  <p className="text-blue-800 font-medium">
                    Due at property:{" "}
                    {formatPrice(
                      booking.accommodation.rooms[0].rates[0]
                        .due_at_accommodation_amount,
                      booking.accommodation.rooms[0].rates[0]
                        .due_at_accommodation_currency
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Cancellation Policy */}
            <div>
              <h2 className="text-lg font-semibold mb-3">
                Cancellation Policy
              </h2>
              <div className="bg-gray-50 rounded p-4 space-y-2">
                {booking.accommodation.rooms[0].rates[0].cancellation_timeline.map(
                  (policy, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <p className="font-medium">
                        Free cancellation before{" "}
                        {format(parseISO(policy.before), "d MMM yyyy, HH:mm")}
                      </p>
                      <p className="text-gray-600">
                        Refund: {policy.currency} {policy.refund_amount}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-4">
            {!isNewBooking && (
              <div className="flex gap-4">
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className="flex-1 bg-red-500 text-white py-4 rounded-full hover:bg-red-600 transition-colors text-lg font-semibold"
                >
                  Cancel Trip
                </button>
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className={`flex-1 ${config.primaryColorBg} text-gray-800 py-4 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold`}
                >
                  Modify Trip
                </button>
              </div>
            )}
            <button
              onClick={() => navigate("/hotels")}
              className="w-full bg-gray-100 text-gray-800 py-4 rounded-full hover:bg-gray-200 transition-colors text-lg font-semibold"
            >
              Search for More Properties
            </button>
          </div>

          {/* Support Modal */}
          {isSupportModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">Contact Support</h3>
                  <button
                    onClick={() => setIsSupportModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <p className="text-gray-600 mb-6">
                  Please contact our support team for assistance with your
                  booking:
                </p>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-gray-600"
                      />
                      <span>{config.supportInfo.phone}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(config.supportInfo.phone)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-gray-600"
                      />
                      <span>{config.supportInfo.email}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(config.supportInfo.email)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mt-6">
                  Our support team is available 24/7 to assist you with any
                  changes or cancellations.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingConfirmation;
